import { useEffect, useRef, type RefObject } from 'react'

/**
 * The two elements the reader hands back.
 *
 * They are separate because they do separate jobs: the platform makes the
 * `<dialog>` modal, and the surface inside it is what goes fullscreen. See
 * `enterFullscreen` for why those cannot be the same element.
 */
export interface FullscreenDialog {
  readonly dialogRef: RefObject<HTMLDialogElement | null>
  readonly surfaceRef: RefObject<HTMLDivElement | null>
}

/**
 * Asks for real fullscreen, and accepts no for an answer.
 *
 * THE SURFACE INSIDE THE DIALOG. NOT THE DIALOG, AND — THE PART THAT WAS A BUG —
 * NOT THE DOCUMENT ELEMENT.
 *
 * Chromium refuses `requestFullscreen()` on a `<dialog>` outright: `TypeError:
 * Dialog elements are invalid`. That is still true, so the reader cannot simply
 * fullscreen itself, and this used to answer by fullscreening
 * `document.documentElement` instead, on the reasoning that a modal dialog is
 * already in the top layer painting over everything and fullscreen only removes
 * the browser's chrome from around it.
 *
 * THAT REASONING WAS WRONG, AND IT BROKE THE BOOK. The top layer is an ORDERED
 * set, painted in the order elements join it. `showModal()` put the dialog in
 * first; `requestFullscreen()` then put `<html>` in second, which paints LAST —
 * so the entire document, poem route and all, was painted on top of the reader.
 * The book opened with the page it was launched from superimposed over every line
 * of it: two titles, two copies of the verse, the sticky header and the poem's
 * emblems straight across the text.
 *
 * Fullscreening a surface INSIDE the dialog cannot reproduce that whatever the
 * order. The surface joins the top layer after the dialog and so paints above it,
 * which is exactly right — it is the dialog's own content — and the document is
 * never in the top layer at all.
 *
 * TWO REFUSALS ARE ROUTINE, and neither is an error worth reporting. iOS Safari
 * ships no `Element.requestFullscreen` at all — on an iPhone this has always been
 * a `<video>`-only API — and every engine refuses the promise when the call is not
 * tied to a user gesture or when a `Permissions-Policy` forbids it. The site's own
 * policy allows it (`fullscreen=(self)` in `src/edge/csp.ts`), but a page embedded
 * elsewhere would not inherit that.
 *
 * NEITHER REFUSAL COSTS THE READER ANYTHING, which is what makes them safe to
 * swallow: the dialog is already covering the viewport, and fullscreen only takes
 * away the browser's own chrome. The Lighthouse gate is `errors-in-console: 0`, so
 * an unhandled rejection here would fail the build.
 */
function enterFullscreen(surface: HTMLElement | null): void {
  if (surface === null) return
  if (typeof surface.requestFullscreen !== 'function') return

  surface.requestFullscreen().catch(() => {
    // Refused. The dialog is already full-viewport; there is nothing to recover.
  })
}

/**
 * Hands fullscreen back when the book closes.
 *
 * Both guards are load-bearing rather than defensive. An engine with no
 * `requestFullscreen` has no `exitFullscreen` either, and `fullscreenElement` is
 * `null` whenever the request above was refused — calling `exitFullscreen` in
 * either state throws or rejects for nothing.
 */
function leaveFullscreen(): void {
  if (typeof document.exitFullscreen !== 'function') return
  if (document.fullscreenElement === null) return

  document.exitFullscreen().catch(() => {
    // Already left — a reader pressing F11 or the Escape key beats us to it.
  })
}

/**
 * Opens a `<dialog>` as a modal, in fullscreen, for as long as it is mounted.
 *
 * A NATIVE `<dialog>` AND `showModal()`, NOT A DIV WITH A HAND-ROLLED TRAP.
 * rules/accessibility.md § 4 requires focus to move into an overlay, cycle within
 * it, never reach inert background content, and return to the invoker on close,
 * with `Escape` always closing. The platform does all five, in every engine,
 * including the parts a JavaScript focus trap gets wrong: content made genuinely
 * inert rather than merely `aria-hidden`, and the browser's own focus ring
 * behaviour preserved.
 *
 * FULLSCREEN ALONE WOULD NOT HAVE DONE IT. `requestFullscreen` on a plain element
 * hides the rest of the page visually but leaves it focusable — tab far enough and
 * focus lands on controls nobody can see. Modality is what makes the reader safe;
 * fullscreen is what makes it a book.
 *
 * MOUNTED ONLY WHILE OPEN, which is why this needs no `isOpen` argument. The
 * container renders the reader conditionally, so the twenty-three poems inside it
 * never reach the prerendered HTML — where they would be duplicate content on
 * every route the launcher appears on, and dead weight on the LCP of a page that
 * is not showing them.
 */
export function useFullscreenDialog(onClose: () => void): FullscreenDialog {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)

  /**
   * Opening and closing, tied to the mount and to nothing else.
   *
   * SEPARATE FROM THE LISTENER BELOW, deliberately. `showModal()` on a dialog
   * that is already open throws `InvalidStateError`, and `leaveFullscreen` in a
   * cleanup would run every time the effect re-ran — so an effect that both opens
   * the dialog and subscribes to it would break the moment `onClose` changed
   * identity. One effect per external system, each with the dependencies that
   * system actually has.
   */
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    dialog.showModal()
    enterFullscreen(surfaceRef.current)

    return () => {
      leaveFullscreen()
    }
  }, [])

  /**
   * `close` rather than `cancel`: it fires for every route out of the dialog —
   * the Escape key, the close button's own `close()` call, and the browser's
   * dismissal — where `cancel` fires only for Escape.
   */
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    dialog.addEventListener('close', onClose)

    return () => {
      dialog.removeEventListener('close', onClose)
    }
  }, [onClose])

  return { dialogRef, surfaceRef }
}
